import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { coercePoiIconSizingRules, DEFAULT_POI_ICON_SIZING_RULES, validatePoiIconSizingRules } from '../../lib/poi-icon-sizing';
import type { PoiIconSizingRule } from '../../types/map';
import AdminPoiTypes from './AdminPoiTypes';

interface Poi {
  id: string;
  name: string;
  type: string;
  latitude: number;
  longitude: number;
  description?: string;
  typeLabel?: string;
  typeColor?: string;
}

interface PoiType {
  name: string;
  label: string;
  color: string;
}

type AdminPoiTab = 'pois' | 'types' | 'icon-sizing';

const TAB_LIST: Array<{ id: AdminPoiTab; label: string }> = [
  { id: 'pois', label: 'POIs' },
  { id: 'types', label: 'POI Types' },
  { id: 'icon-sizing', label: 'POI Icon Sizing' },
];

function readTab(raw: string | null): AdminPoiTab {
  if (raw === 'types' || raw === 'icon-sizing') return raw;
  return 'pois';
}

export default function AdminPois() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = readTab(searchParams.get('tab'));

  const setActiveTab = (tab: AdminPoiTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">POI Management</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Manage points of interest, type definitions, and zoom-based icon sizing.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TAB_LIST.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'pois' && <PoisTab />}
      {activeTab === 'types' && <AdminPoiTypes embedded />}
      {activeTab === 'icon-sizing' && <PoiIconSizingTab />}
    </div>
  );
}

function PoisTab() {
  const [pois, setPois] = useState<Poi[]>([]);
  const [poiTypes, setPoiTypes] = useState<PoiType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', type: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', type: 'lock', latitude: '', longitude: '', description: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [poisRes, typesRes] = await Promise.all([
        api.pois.getAll({ type: typeFilter || undefined, search: search || undefined }),
        api.pois.getTypes(),
      ]);
      setPois(poisRes);
      setPoiTypes(typesRes);
    } catch (err) {
      console.error('Failed to load POIs:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (poi: Poi) => {
    setEditingId(poi.id);
    setEditForm({ name: poi.name, type: poi.type, description: poi.description || '' });
  };

  const handleSave = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.pois.update(editingId, editForm);
      setEditingId(null);
      await loadData();
    } catch (err) {
      console.error('Failed to update POI:', err);
      alert('Failed to update POI');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (poi: Poi) => {
    if (!window.confirm(`Delete POI "${poi.name}"? This cannot be undone.`)) return;
    try {
      await api.pois.delete(poi.id);
      setPois((prev) => prev.filter((p) => p.id !== poi.id));
    } catch (err) {
      console.error('Failed to delete POI:', err);
      alert('Failed to delete POI');
    }
  };

  const handleCreate = async () => {
    const lat = parseFloat(createForm.latitude);
    const lng = parseFloat(createForm.longitude);
    if (!createForm.name || Number.isNaN(lat) || Number.isNaN(lng)) {
      alert('Name, latitude and longitude are required');
      return;
    }
    setSaving(true);
    try {
      await api.pois.create({
        name: createForm.name,
        type: createForm.type,
        latitude: lat,
        longitude: lng,
        description: createForm.description || undefined,
      });
      setCreating(false);
      setCreateForm({ name: '', type: 'lock', latitude: '', longitude: '', description: '' });
      await loadData();
    } catch (err) {
      console.error('Failed to create POI:', err);
      alert('Failed to create POI');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Points of Interest</h2>
        <button onClick={() => setCreating(true)} className="btn btn-primary">
          + Add POI
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search POIs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
        >
          <option value="">All Types</option>
          {poiTypes.map((t) => (
            <option key={t.name} value={t.name}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {creating && (
        <div className="card p-4 space-y-3">
          <h3 className="font-semibold text-slate-900 dark:text-white">Create New POI</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Name *</label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                placeholder="e.g. Braunston Lock 1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Type</label>
              <select
                value={createForm.type}
                onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
              >
                {poiTypes.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Latitude *</label>
              <input
                type="text"
                value={createForm.latitude}
                onChange={(e) => setCreateForm({ ...createForm, latitude: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                placeholder="e.g. 52.2632"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Longitude *</label>
              <input
                type="text"
                value={createForm.longitude}
                onChange={(e) => setCreateForm({ ...createForm, longitude: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                placeholder="e.g. -1.2168"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Description</label>
            <textarea
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary text-sm disabled:opacity-50">
              {saving ? 'Creating...' : 'Create POI'}
            </button>
            <button onClick={() => setCreating(false)} className="btn text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-48 mb-2" />
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-24" />
            </div>
          ))}
        </div>
      ) : pois.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-slate-500 dark:text-slate-400">No POIs found</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-400 hidden sm:table-cell">Location</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pois.map((poi) => (
                  <tr key={poi.id} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    {editingId === poi.id ? (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="w-full px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={editForm.type}
                            onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                            className="px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                          >
                            {poiTypes.map((t) => (
                              <option key={t.name} value={t.name}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400 hidden sm:table-cell">
                          {poi.latitude.toFixed(4)}, {poi.longitude.toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={handleSave} disabled={saving} className="px-2 py-1 text-xs rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50">
                              {saving ? '...' : 'Save'}
                            </button>
                            <button onClick={() => setEditingId(null)} className="px-2 py-1 text-xs rounded bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500">
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 dark:text-white">{poi.name}</div>
                          {poi.description && <div className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{poi.description}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: poi.typeColor || '#94a3b8' }} />
                            <span className="text-slate-600 dark:text-slate-300">{poi.typeLabel || poi.type}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400 hidden sm:table-cell">
                          {poi.latitude.toFixed(4)}, {poi.longitude.toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleEdit(poi)}
                              className="px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(poi)}
                              className="px-2 py-1 text-xs rounded bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PoiIconSizingTab() {
  const [rules, setRules] = useState<PoiIconSizingRule[]>(DEFAULT_POI_ICON_SIZING_RULES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dirty, setDirty] = useState(false);

  const loadRules = useCallback(async () => {
    setLoading(true);
    setSaveError('');
    try {
      const res = await api.pois.getIconSizing();
      setRules(coercePoiIconSizingRules(res));
      setDirty(false);
    } catch (err) {
      console.error('Failed to load POI icon sizing:', err);
      setSaveError('Failed to load icon sizing rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const validationMessage = useMemo(() => {
    const validation = validatePoiIconSizingRules(rules);
    return validation.ok ? '' : validation.error;
  }, [rules]);

  const updateRule = (index: number, field: 'zoomFrom' | 'zoomTo' | 'svgSizePx', value: string) => {
    const nextValue = Number(value);
    setRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, [field]: Number.isFinite(nextValue) ? nextValue : 0 } : rule)));
    setDirty(true);
    setSaveError('');
  };

  const addRule = () => {
    setRules((prev) => [...prev, { zoomFrom: 0, zoomTo: 0, svgSizePx: 16 }]);
    setDirty(true);
  };

  const removeRule = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleReset = () => {
    loadRules();
  };

  const handleSave = async () => {
    const validation = validatePoiIconSizingRules(rules);
    if (!validation.ok) {
      setSaveError(validation.error);
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const updated = await api.pois.updateIconSizing(validation.rules);
      setRules(coercePoiIconSizingRules(updated));
      setDirty(false);
    } catch (err: any) {
      console.error('Failed to save POI icon sizing:', err);
      setSaveError(err?.message || 'Failed to save icon sizing rules');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">POI Icon Sizing</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Define SVG icon sizes for zoom ranges (must fully cover zoom 0-22 with no gaps).
          </p>
        </div>
        <button type="button" onClick={addRule} className="btn btn-primary">
          + Add Zoom Level
        </button>
      </div>

      {(saveError || validationMessage) && (
        <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {saveError || validationMessage}
        </div>
      )}

      {loading ? (
        <div className="card p-4 text-sm text-slate-500 dark:text-slate-400">Loading icon sizing rules...</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Zoom From</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Zoom To</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-400">SVG Size (px)</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, index) => (
                <tr key={`${rule.id || 'new'}-${index}`} className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min={0}
                      max={22}
                      value={rule.zoomFrom}
                      onChange={(e) => updateRule(index, 'zoomFrom', e.target.value)}
                      className="w-24 px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min={0}
                      max={22}
                      value={rule.zoomTo}
                      onChange={(e) => updateRule(index, 'zoomTo', e.target.value)}
                      className="w-24 px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min={4}
                      max={256}
                      value={rule.svgSizePx}
                      onChange={(e) => updateRule(index, 'svgSizePx', e.target.value)}
                      className="w-28 px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => removeRule(index)}
                      className="px-2 py-1 text-xs rounded bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={handleSave} disabled={saving || loading} className="btn btn-primary disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Sizing Rules'}
        </button>
        <button type="button" onClick={handleReset} disabled={saving || loading} className="btn disabled:opacity-50">
          Reset
        </button>
      </div>
    </div>
  );
}
